import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiResponse } from '@core/models/api-response.model';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { GameAccountMember } from '@core/models/game-account-member.model';
import { GameProvider } from '@core/models/game-provider.model';
import { GameCategory } from '@core/models/game-category.model';
import { Game } from '@core/models/game.model';

@Injectable()
export class GameProviderHttpService {

  blacklistedGameProviders$ = new BehaviorSubject<any>(null);
  selectedSubCategories$ = new BehaviorSubject<any>(null);
  selectionMode: string;

  constructor(private http: HttpClient) { }

  getGameProviders(categories?: number[]): Observable<GameProvider[]> {
    let params = '';
    if(categories !== undefined && categories.length >= 1) {
      const uniqueCategories = Array.from(new Set(categories));
      uniqueCategories.forEach((id, index) => params += `&category_id[${index}]=${id}`)
    }
    return this.http.get<ApiResponse>(`/gameprovider?paginate=false${params}`).pipe(
      map(res => res.data.rows)
    );
  }

  getGameProvidersByName(): Observable<GameProvider[]> {
    return this.http.get<ApiResponse>('/gameprovider/providerNames').pipe(
      map(res => res.data)
    );
  }

  getGameProvidersCategory(category: string): Observable<GameProvider[]> {
    return this.http.get<ApiResponse>(`/gameprovider?category_code=${category}&paginate=false`).pipe(
      map(res => res.data.rows)
    );
  }

  getGameProviderBetLimit(game_provider_code?: string, currency_code?: string) {
    return this.http.get<ApiResponse>(`/betlimit?game_provider_code=${game_provider_code}&currency_code=${currency_code}`).pipe(
      map(res => res.data.rows)
    );
  }

  getGameCategories(status: boolean = true): Observable<GameProvider[]> {
    let url = '/gameprovider/gamecategories?paginate=false';
    if (status) {
      url += '&status=1';
    }
    return this.http.get<ApiResponse>(url).pipe(
      map(res => res.data.rows)
    );
  }

  getGameCategory(id: number | string): Observable<GameCategory[]> {
    return this.http.get<ApiResponse>(`/gameprovider/getgamecategory/${id}`).pipe(
      map(res => res.data.rows)
    );
  }

  getGameProviderBalance(params: string){
    return this.http.get<ApiResponse>(`/gameprovider/balance${params}`).pipe(
      map(res => res.data.data)
    );
  }

  getGameAccountsOfMember(username: string, settingsCurrencyId: number): Observable<GameAccountMember[]> {
    return this.http.get<ApiResponse>(`/gameprovider/account?username=${username}&currency_id=${settingsCurrencyId}`).pipe(
      map(res => res.data)
    );
  }

  getGameDataBalanceOfMember(memberId: number, gameProviderId: number): Observable<GameAccountMember> {
    return this.getGameProviderBalance(`?member_account_id=${memberId}&game_provider_id=${gameProviderId}`);
  }

  getSubCategories(providerCodes: any[], categoryCode?: string, status?: number): Observable<any[]> {
    let params = '', categoryParams = '', statusParams = '';
    providerCodes.forEach((code: string, index: number) => {
      params += `game_provider_code[${index}]=${code}&`;
    });
    categoryCode !== undefined && categoryCode != null ? categoryParams = `category_code=${categoryCode}` : '';
    status !== undefined ? statusParams = `&status=${status}`: '';
    return this.http.get<ApiResponse>(`/gameprovider/subcategories?${params}${categoryParams}${statusParams}`).pipe(
      map(res => res.data.rows)
    );
  }

  getFreeSpinGameProvider(): Observable<GameProvider[]> {
    return this.http.get<ApiResponse>(`/gameprovider/freespingameproviders`).pipe(
      map(res => res.data.rows)
    );
  }

  getFreeSpinGameList(code: string): Observable<Game[]> {
    return this.http.get<ApiResponse>(`/gameprovider/freespingame/${code}`).pipe(
      map(res => res.data.rows)
    );
  }

  getFreeRoundBetValues(params: {
    game_provider_code: string;
    game_code: string[];
    currency?: string;
  }): Observable<any[]> {
    return this.http.post<ApiResponse>('/promotion/free-round/bet-values', params).pipe(
      map(res => {
        // Treat any explicit non-success status as an error (status 1 is the
        // success sentinel). status 0, 2, etc. carry a message we want surfaced
        // rather than parsed as a (possibly empty) value list.
        const status = res.data?.status;
        if (status !== undefined && status !== null && status !== 1) {
          throw res.data;
        }

        const values = res.data?.data?.bet_values ?? res.data?.rows ?? res.data?.bet_values ?? res.data?.betValues ?? res.data?.values ?? res.data ?? [];
        if (!Array.isArray(values)) {
          throw {
            status: 0,
            messages: ['Failed to load bet values. Please try again.'],
          };
        }

        return values;
      })
    );
  }

}
